import {
  CloudActions,
  CloudErrors,
  UserServiceUrls,
  AuthServiceUrls,
  FlowjoCommons,
} from '@portal/portal-core';
import { call, put } from 'redux-saga/effects';

import {
  updateUserProfileSuccess,
  updateUserProfileFailure,
  changeUserPasswordSuccess,
  changeUserPasswordFailure,
} from '../actions';
import { sendAction } from '../utils';

const { Alerts } = FlowjoCommons;

// Minimal shape of the user client config threaded into these sagas; read by the
// still-untyped `.js` utils that build requests from `baseApiUrl` / `getAuthToken`.
interface UserClientConfig {
  baseApiUrl?: string;
  getAuthToken: () => string | null | undefined;
}

interface UpdateUserProfileAction {
  payload: {
    userId: string;
    updates: Record<string, unknown>;
  };
}

interface ChangeUserPasswordAction {
  payload: {
    userId: string;
    currentPassword: string;
    newPassword: string;
  };
}

/**
 * Saga to handle user profile updates (firstname, lastname, countryCode)
 */
export function* updateUserProfileSaga(
  config: UserClientConfig,
  action: UpdateUserProfileAction,
): Generator<unknown, void, unknown> {
  const { userId, updates } = action.payload;
  const url = UserServiceUrls.updateUserFieldsUrl(config.baseApiUrl, userId);
  const serverAction = CloudActions.userUpdateFields(userId, updates);

  try {
    // `sendAction` is an untyped `.js` helper -> its resolved value is `any`.
    const res = (yield call(sendAction, config, serverAction, url)) as {
      succeeded?: boolean;
      failed?: boolean;
      data?: unknown;
      error?: unknown;
    };
    const { succeeded, failed, data, error } = res;

    if (failed) {
      yield put(updateUserProfileFailure(error));
    } else if (succeeded) {
      // Update the user in Redux store
      yield put(CloudActions.setUser(data));
      yield put(updateUserProfileSuccess(data));
    }
  } catch (error) {
    yield put(updateUserProfileFailure(error));
  }
}

/**
 * Saga to handle password change
 */
export function* changeUserPasswordSaga(
  config: UserClientConfig,
  action: ChangeUserPasswordAction,
): Generator<unknown, void, unknown> {
  const { userId, currentPassword, newPassword } = action.payload;
  const url = AuthServiceUrls.changePasswordUrl(config.baseApiUrl);
  const serverAction = CloudActions.changePassword(userId, currentPassword, newPassword);

  try {
    // `sendAction` is an untyped `.js` helper -> its resolved value is `any`.
    const res = (yield call(sendAction, config, serverAction, url)) as {
      succeeded?: boolean;
    };

    if (res.succeeded) {
      yield put(changeUserPasswordSuccess());

      // Set success alert
      const successAlert = Alerts.Alert.AlertStamp({
        id: CloudErrors.cloudErrorTypes.SUCCESS_CHANGE_PASSWORD,
        date: null,
        type: CloudErrors.cloudErrorTypes.SUCCESS_CHANGE_PASSWORD,
      });
      yield put(Alerts.AlertActions.setAlert(successAlert));
    } else {
      yield put(changeUserPasswordFailure('Cannot change password'));

      // Set error alert
      const errorAlert = Alerts.Alert.AlertStamp({
        id: CloudErrors.cloudErrorTypes.CANNOT_CHANGE_PASSWORD,
        date: null,
        type: CloudErrors.cloudErrorTypes.CANNOT_CHANGE_PASSWORD,
      });
      yield put(Alerts.AlertActions.setAlert(errorAlert));
    }
  } catch (error) {
    yield put(changeUserPasswordFailure(error));

    // Set error alert
    const errorAlert = Alerts.Alert.AlertStamp({
      id: CloudErrors.cloudErrorTypes.CANNOT_CHANGE_PASSWORD,
      date: null,
      type: CloudErrors.cloudErrorTypes.CANNOT_CHANGE_PASSWORD,
    });
    yield put(Alerts.AlertActions.setAlert(errorAlert));
  }
}
